import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronLeft, ArrowRight, HelpCircle } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import FaqJsonLd from "@/components/FaqJsonLd";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { getGuide } from "@/data/guides";

export default function GuideArticle() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  useSeo({
    title: guide?.metaTitle || "Guide — PROXIreduc-28",
    description: guide?.metaDescription || "",
    canonicalPath: guide ? `/guides/${guide.slug}` : undefined,
    image: "/og-image.jpg",
    type: "article",
  });

  if (!guide) return <Navigate to="/guides" replace />;

  return (
    <div className="bg-[#FAF8F5] min-h-screen" data-testid="guide-article-page">
      <FaqJsonLd faq={guide.faq} />
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Guides", path: "/guides" }, { name: guide.title }]} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <Link to="/guides" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="guide-back-link">
          <ChevronLeft size={15} /> Tous les guides
        </Link>

        <span className="mt-6 block text-[11px] font-bold uppercase tracking-wide text-[#E05638]">{guide.category}</span>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="guide-title">{guide.h1}</h1>
        <p className="mt-4 text-lg text-slate-700 leading-relaxed">{guide.intro}</p>

        <div className="mt-8 space-y-8">
          {guide.sections.map((s, i) => (
            <section key={i} data-testid={`guide-section-${i}`}>
              <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900">{s.h2}</h2>
              {(s.paragraphs || []).map((p, j) => (
                <p key={j} className="mt-3 text-slate-700 leading-relaxed">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-2 text-slate-700">
                      <ArrowRight size={16} className="text-[#E05638] shrink-0 mt-1" /> <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* FAQ visible (alignée au schéma FAQPage) */}
        <section className="mt-12" data-testid="guide-faq">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <HelpCircle size={20} className="text-[#E05638]" /> Questions fréquentes
          </h2>
          <div className="mt-4 space-y-4">
            {guide.faq.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="font-bold text-slate-900">{f.q}</div>
                <p className="mt-1 text-sm text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Maillage interne */}
        <section className="mt-12" data-testid="guide-related">
          <h2 className="font-heading text-lg font-extrabold text-slate-900">À lire aussi</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {guide.related.map((r) => (
              <Link key={r.to} to={r.to} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full bg-white border border-slate-200 hover:border-slate-400 text-slate-800">
                {r.label} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </section>

        {guide.cta && (
          <div className="mt-12 rounded-2xl bg-slate-900 text-white p-6 md:p-8 text-center">
            <p className="font-heading text-xl font-extrabold">Prêt à passer à l'action ?</p>
            <Link to={guide.cta.to} className="mt-4 inline-flex items-center gap-2 bg-[#E05638] hover:bg-[#c94a2f] text-white font-extrabold h-12 px-6 rounded-xl" data-testid="guide-cta">
              {guide.cta.label} <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </article>
    </div>
  );
}
